import _compact from "lodash/compact";
import { TFunction } from "i18n";
import { UserClaimTypes, UserManager } from "core";
import { IBasicInfoViewModel } from "pages/MainPage/components/BasicInfoSection/viewModels";
import { ConverterBasicInfo } from "./types";

type RearrangeDisabilitiesParams = {
  disabilities: string[] | undefined;
  basicInfo: ConverterBasicInfo;
  isVisibilitySettingSuppressed: boolean;
  t: TFunction;
};

function rearrangeDisabilities({
  disabilities = [],
  basicInfo,
  isVisibilitySettingSuppressed,
  t,
}: RearrangeDisabilitiesParams): string[] {
  if (!basicInfo.isVisibleDisability && !isVisibilitySettingSuppressed) {
    return [];
  }

  if (!basicInfo.hasDisability) {
    return [];
  }

  const { disabilityInfo = "" } = basicInfo;
  const fallback =
    disabilities.length === 0 && disabilityInfo.trim() === ""
      ? t("common:label.yes")
      : undefined;

  return _compact([...disabilities, fallback, disabilityInfo]);
}

function rearrangeGenders(
  basicInfo: ConverterBasicInfo,
  isVisibilitySettingSuppressed: boolean,
  genders: string[] = []
): string[] {
  if (!basicInfo.isVisibleGender && !isVisibilitySettingSuppressed) {
    return [];
  }

  return _compact([...genders, basicInfo.genderInfo]);
}

type ApplyVisibilityAwareFieldsParams = {
  model: Readonly<Partial<IBasicInfoViewModel>>;
  basicInfo: ConverterBasicInfo;
  isViewMode: boolean;
  t: TFunction;
};

export function applyVisibilityAwareFields({
  model,
  basicInfo,
  isViewMode,
  t,
}: ApplyVisibilityAwareFieldsParams): Partial<IBasicInfoViewModel> {
  const isGenderVisible =
    !isViewMode || UserManager.hasClaimValue(UserClaimTypes.isGenderVisible);
  const isDisabilityVisible =
    !isViewMode ||
    UserManager.hasClaimValue(UserClaimTypes.isDisabilitiesVisible);

  return {
    disabilities: rearrangeDisabilities({
      disabilities: model.disabilities,
      basicInfo,
      isVisibilitySettingSuppressed: isDisabilityVisible,
      t,
    }),
    genders: rearrangeGenders(basicInfo, isGenderVisible, model.genders),
  };
}

export function mapEthnicity(
  model: Readonly<Partial<IBasicInfoViewModel>>,
  basicInfo: ConverterBasicInfo,
  isViewMode: boolean
): Partial<IBasicInfoViewModel> {
  const isHeritageVisible =
    !isViewMode || basicInfo.isVisibleHeritageAndEthnicity;

  if (
    !isHeritageVisible ||
    (!model.ethnicBackground?.length &&
      !model.heritageAndEthnicity?.length &&
      !basicInfo.heritageAndEthnicityInfo)
  ) {
    return {};
  }

  return {
    ethnicity: _compact([
      ...(model.ethnicBackground || []),
      ...(model.heritageAndEthnicity || []),
      basicInfo.heritageAndEthnicityInfo || "",
    ]),
  };
}
